import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

export default class extends Controller {
  static targets = ["buttonsContainer"]
  static values = {
    invitationKey: String
  }

  declare buttonsContainerTarget: HTMLElement
  declare invitationKeyValue: string

  async applySlate(event: Event): Promise<void> {
    event.preventDefault()
    
    const button = event.currentTarget as HTMLButtonElement
    const slateId = button.dataset.slateId
    
    if (!slateId) {
      console.error('Slate ID not found. Button dataset:', button.dataset)
      alert('Invalid group selection')
      return
    }

    try {
      // Disable all buttons and show loading state
      this.setLoading(button, true)
      this.disableAllButtons(true)
      
      // Make AJAX request
      const response = await fetch(`/ballot/${this.invitationKeyValue}/slates/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken(),
          'Accept': 'text/vnd.turbo-stream.html'
        },
        body: JSON.stringify({
          choice_group_id: slateId
        })
      })
      
      if (!response.ok) {
        // Network error - show fallback alert
        alert(`Error: ${response.status}`)
        this.disableAllButtons(false)
        return
      }
      
      // Get Turbo Stream response text
      const text = await response.text()
      
      // Extract selections from custom turbo-stream action before rendering
      // Format: <turbo-stream action="slate_selections" target="..."><template>JSON</template></turbo-stream>
      const selectionsMatch = text.match(/<turbo-stream[^>]*action="slate_selections"[^>]*><template>([^<]*)<\/template><\/turbo-stream>/)
      let selections: Record<string, string[]> | null = null
      
      if (selectionsMatch && selectionsMatch[1]) {
        try {
          selections = JSON.parse(selectionsMatch[1])
        } catch (e) {
          console.error('[Slate] Failed to parse selections JSON:', e)
        }
      }
      
      // Remove the custom action from the stream before rendering (Turbo doesn't know this action)
      const cleanedText = text.replace(/<turbo-stream[^>]*action="slate_selections"[^>]*><template>[^<]*<\/template><\/turbo-stream>/g, '')
      
      // Render remaining Turbo Stream content (if any)
      if (cleanedText.trim()) {
        Turbo.renderStreamMessage(cleanedText)
      }
      
      // Apply selections to form
      if (selections) {
        this.applySelectionsToForm(selections)
      }
      
    } catch (error) {
      console.error('Failed to apply slate:', error)
      alert(error instanceof Error ? error.message : 'Failed to apply group')
    } finally {
      this.setLoading(button, false)
      this.disableAllButtons(false)
    }
  }

  private applySelectionsToForm(selections: Record<string, string[]>): void {
    const checkedInputs: HTMLInputElement[] = []
    
    // Clear all existing selections first
    Object.keys(selections).forEach(questionId => {
      const questionElement = document.getElementById(`question_${questionId}`)
      if (!questionElement) return
      
      // Uncheck all checkboxes and radio buttons in this question
      const inputs = questionElement.querySelectorAll<HTMLInputElement>(
        'input[type="checkbox"]:not([id*="abstain"]), input[type="radio"]:not([id*="abstain"])'
      )
      inputs.forEach(input => {
        input.checked = false
        // Remove selected class from parent row if it exists
        const row = input.closest('.question_row, tr')
        if (row) {
          row.classList.remove('selected')
        }
      })
    })
    
    // Apply new selections - collect all inputs first, then trigger events
    Object.entries(selections).forEach(([questionId, answerIds]) => {
      answerIds.forEach(answerId => {
        const questionElement = document.getElementById(`question_${questionId}`)
        if (!questionElement) return
        
        // Try to find checkbox input first (multi-choice)
        // Format: ballot[data[questions[{q_id}][answers][{a_id}]]]
        // Rails generates ID: ballot_data_questions_{q_id}_answers_{a_id}
        let input = questionElement.querySelector<HTMLInputElement>(
          `input[type="checkbox"][id*="answers_${answerId}"]`
        )
        
        // If not found, try radio button (single-choice)
        // Format: ballot[data[questions[{q_id}][answer]]] with value={a_id}
        if (!input) {
          input = questionElement.querySelector<HTMLInputElement>(
            `input[type="radio"][value="${answerId}"]`
          )
        }
        
        if (input) {
          input.checked = true
          
          // Add selected class to parent row if it exists
          const row = input.closest('.question_row, tr')
          if (row) {
            row.classList.add('selected')
          }
          
          checkedInputs.push(input)
        }
      })
    })
    
    // Dispatch a single custom event after ALL selections are made
    // This avoids triggering per-input validations while still notifying
    // components like choice-group-instructions about the bulk selection
    const form = this.element.closest('form')
    if (form) {
      form.dispatchEvent(new CustomEvent('slate:selections-applied', {
        bubbles: true,
        detail: { inputCount: checkedInputs.length }
      }))
    }
  }

  private setLoading(button: HTMLButtonElement, loading: boolean): void {
    if (loading) {
      button.classList.add('ballots--slates-component__button--loading')
      button.disabled = true
    } else {
      button.classList.remove('ballots--slates-component__button--loading')
    }
  }

  private disableAllButtons(disabled: boolean): void {
    const buttons = this.buttonsContainerTarget.querySelectorAll('button')
    buttons.forEach(button => {
      button.disabled = disabled
    })
  }



  private csrfToken(): string {
    const meta = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement
    return meta ? meta.content : ''
  }
}
